import store from '@/store/store';
import eventbus from 'tiny-emitter/instance';
import first from 'lodash/first';
import { TEXT, TYPE, GA, SECTION, RENDERER_MODE, SECTIONSTATUS } from '@/constants';
import { Section } from './section';
import Matrix from '@/core/Matrix';
import { lastUsedSectionFontSize } from '@/core/utils';
import { preloadThumbnail } from '@/core/drawing/utils';

export default class PolygonalSection extends Section {
  constructor(params) {
    super(params);
    this.initView();
  }

  get subtype() {
    return TYPE.POLYGON;
  }

  getPathArray(points, smoothing) {
    const firstCoordinate = first(points);
    points.pop();
    points.push(firstCoordinate);
    let d = '';
    points.forEach((point, idx) => {
      d += idx === 0 ? `M ${point[0]},${point[1]}` : `${this.bezierCommand(point, idx, points, smoothing)}`;
    });
    return d;
  }

  line(pointA, pointB) {
    const lengthX = pointB[0] - pointA[0];
    const lengthY = pointB[1] - pointA[1];
    return {
      length: Math.sqrt(lengthX ** 2 + lengthY ** 2),
      angle: Math.atan2(lengthY, lengthX),
    };
  }

  controlPoint(current, previous, next, reverse, smoothing) {
    const p = previous || current;
    const n = next || current;
    const o = this.line(p, n);
    const angle = o.angle + (reverse ? Math.PI : 0);
    const length = o.length * smoothing;
    const x = current[0] + Math.cos(angle) * length;
    const y = current[1] + Math.sin(angle) * length;
    return [x, y];
  }

  bezierCommand(point, i, a, smoothing) {
    const cps = this.controlPoint(a[i - 1], a[i - 2], point, false, smoothing);
    const cpe = this.controlPoint(point, a[i - 1], a[i + 1], true, smoothing);
    return ` C ${cps[0]},${cps[1]} ${cpe[0]},${cpe[1]} ${point[0]},${point[1]}`;
  }

  plot(onResizing = false) {
    const { shape, smoothing } = this.attributes;
    //support for polygon to be converted to path
    let shapeArray = [];
    if (typeof shape === 'string') {
      shape.split(' ').forEach((coord) => {
        const coordSplit = coord.split(',');
        shapeArray.push([parseFloat(coordSplit[0], 10), parseFloat(coordSplit[1], 10)]);
      });
    } else {
      shapeArray = shape.value;
    }
    let pathArray = '';
    if (onResizing) {
      const points = shapeArray;
      points.forEach((point, idx) => {
        pathArray += idx === 0 ? `M ${point[0]},${point[1]}` : `L ${point[0]},${point[1]}`;
      });
    } else {
      pathArray = this.getPathArray(shapeArray, smoothing);
    }
    if (!this.shape) {
      this.shape = this.canvas.path(pathArray);
    } else {
      this.shape.plot(pathArray);
    }
    this.attributes.points = this.shape.array();
  }

  initView() {
    const { transform, shapetransform, scale } = this.attributes;
    this.plot();
    this.view.add(this.shape).addClass(`section-shape section-${this.subtype}`);
    this.view.transform(transform);
    this.shape.transform(shapetransform);
    this.recalculateEdgeOffsets();
    if (!RENDERER_MODE) {
      this.shape.attr({ stroke: SECTION.DEFAULTSTROKECOLOR });
    }
    this.initLabel();
    if (this.attributes.color) {
      this.changeFillColor(this.attributes.color, false);
      this.changeLabelStroke(this.attributes.label.stroke, false);
    }
    if (this.attributes.opacity !== undefined) {
      this.changeOpacity(this.attributes.opacity, false);
    }
    if (this.categoryId) {
      this.applyCategoryColor(this.app.getCategoryColor(this.categoryId));
    }
    const lthis = this;
    let hoverTime = null;

    const onClick = function (evt) {
      if (
        RENDERER_MODE &&
        !lthis.isInside &&
        !lthis.app.pinchZooming &&
        !store.state.seatmap.isPanning &&
        store.state.seatmap.isClickable &&
        !lthis.attributes.ga &&
        !lthis.category?.bestAvailable &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        let point = lthis.svg.node.createSVGPoint();
        point.x = evt.clientX;
        point.y = evt.clientY;
        point = point.matrixTransform(lthis.svg.node.getScreenCTM().inverse());
        lthis.app.currentSection = lthis;
      }

      if (
        RENDERER_MODE &&
        !lthis.isInside &&
        !lthis.attributes.ga &&
        !lthis.category?.bestAvailable &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        lthis.app.zoomManager.zoomAndCenterBox(lthis.view.rbox(lthis.app.canvas));
      }

      if (RENDERER_MODE && !lthis.isInside && lthis.app.props.onSectionClick) {
        lthis.app.props.onSectionClick(lthis, lthis.category?.bestAvailable === true);
      }

      if (
        RENDERER_MODE &&
        (lthis.category?.bestAvailable || lthis.attributes.ga) &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        eventbus.emit('show:sectionAlert', lthis);
      }
    };
    this.shape.on('click', onClick);
    /*this.shape.on('touchstart', (evt) => {
      setTimeout(onClick(evt), 900);
    });*/

    this.shape.mouseover(function (e) {
      if (!lthis.isInside) {
        if (RENDERER_MODE) {
          preloadThumbnail(lthis.uuid, lthis.app);
          lthis.canvas.attr({ cursor: 'pointer' });
          lthis.addBorder();
          if (!lthis.app.isMobileDevice) {
            hoverTime = setTimeout(() => {
              eventbus.emit('tooltip:show', lthis, e);
            }, 200);
          }

          if (lthis.app.props.onSectionOver) {
            lthis.app.props.onSectionOver(lthis);
          }
        } else {
          lthis.highlight();
        }
      }
    });

    this.shape.on('touchmove', function () {
      eventbus.emit('tooltip:hide', lthis);
    });
    this.shape.mousemove(function (e) {
      if (RENDERER_MODE && !lthis.isInside && !lthis.app.isMobileDevice) {
        preloadThumbnail(lthis.uuid, lthis.app);
        clearTimeout(hoverTime);
        eventbus.emit('tooltip:hide', lthis, e);
        hoverTime = setTimeout(() => {
          eventbus.emit('tooltip:show', lthis, e);
        }, 200);
      }
    });
    this.shape.mouseout(function (e) {
      if (!RENDERER_MODE) {
        lthis.unhighlight();
      } else if (RENDERER_MODE) {
        lthis.removeBorder();
        if (lthis.app.props.onSectionOut) {
          lthis.app.props.onSectionOut(lthis);
        }
      }
      clearTimeout(hoverTime);
      eventbus.emit('tooltip:hide', lthis, e);
    });

    if (scale) {
      this.changeScale(scale * 100, 100, true, true);
    }
    //this.updateConflictedZone();
  }

  toJSON() {
    const { uuid, type, subtype, sid } = this;
    const transform = this.view.transform('matrix');
    const shapetransform = this.shape.transform('matrix');
    const labeltransform = this.label.transform('matrix');
    const attributes = Object.assign({}, this.attributes, {
      transform,
      shapetransform,
      labeltransform,
    });
    return PolygonalSection.filteredJSON({
      uuid,
      sid,
      type,
      subtype,
      attributes,
    });
  }

  static fromJSON(params) {
    return PolygonalSection.create(params);
  }

  static create(params) {
    const attributes = {
      label: Object.assign({}, GA.LABEL, {
        text: 'Section',
        size: lastUsedSectionFontSize.value,
        transform: '',
        fill: SECTION.DEFAULTFILLCOLOR,
        stroke: SECTION.DEFAULTSTROKECOLOR,
      }),
      shape: '',
      color: SECTION.DEFAULTFILLCOLOR,
      points: [],
      smoothing: 0,
      transform: '',
      shapecenter: [],
      shapetransform: '',
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    return new PolygonalSection(params);
  }

  bbox() {
    const bbox = this.view.bbox();
    return bbox.transform(new Matrix().transform({ rotate: this.attributes.rotation, ox: bbox.cx, oy: bbox.cy }));
  }

  backupShape() {
    const { points, rotation } = this.attributes;
    const matrix = this.view.transform('matrix');
    this.backup = { points: points.value.flat(), rotation, matrix };
  }

  restoreBackup() {
    if (this.backup) {
      const { points, rotation, matrix } = this.backup;
      this.shape.plot(points);
      Object.assign(this.attributes, { points: this.shape.array(), rotation });
      const { cx, cy } = this.shape.bbox();
      this.view.untransform().transform(matrix);
      this.label.attr({
        x: cx,
        y: cy,
      });
    }
  }
}
